'use strict';

define('vb/private/services/servicesManager',[
  'vb/private/configLoader',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/services/endpointReferenceFactory',
  'vb/private/stateManagement/application',
  'vb/private/stateManagement/router',
],
(
  ConfigLoader,
  Log,
  Utils,
  EndpointReferenceFactory,
  Application,
  Router,
) => {
  /**
   * @typedef {import('vb/private/services/endpointReference').default} EndpointReference
   * @typedef {import('vb/private/services/endpoint').default} Endpoint
   * @typedef {import('vb/private/services/services').default} Services
   */

  const logger = Log.getLogger('/vb/private/services/servicesManager');
  /**
   * this is the interface for getting endpoints for the active container (previously, Services was a singleton).
   * Also where Service Providers are registered (ultimately live in the Application's Services property).
   *
   */
  class ServicesManager {
    /**
     * Recover an extension's services for a qualified endpoint when its provider is missing from a partial digest.
     * Service visibility, including publicAccess promotion, is defined in the provider's service definition. That
     * definition is available only after the provider has been materialized, so visibility is checked by the normal
     * service-resolution path rather than pre-filtering recovery by extension dependencies.
     *
     * @param {EndpointReference} endpointReference
     * @returns {Promise<Services|undefined>}
     * @private
     */
    static _getExtensionServices(endpointReference) {
      const extensionId = endpointReference.namespace;
      const extensionServices = Application.getExtensionServices(extensionId);
      if (extensionServices) {
        return Promise.resolve(extensionServices);
      }

      const registry = /** @type {*} */ (ConfigLoader.extensionRegistry);
      // Without a registry that can recover the full manifest, an unmaterialized provider cannot be loaded.
      if (!registry || typeof registry.loadFullManifest !== 'function') {
        return Promise.resolve(undefined);
      }

      const registerExtensionServices = async (extension) => {
        if (extension && !Application.extensions[extensionId]) {
          await Application.loadExtensions();
        }
        return Application.getExtensionServices(extensionId);
      };

      // The provider can be present in the partial registry but not yet materialized on the application.
      // In that case, load it through the normal application-extension path instead of upgrading the registry to
      // the full manifest.
      const extension = registry.getExtensionById(extensionId);
      if (extension) {
        return registerExtensionServices(extension);
      }

      /**
       * @returns {string[]} unique extension ids that can reference the service provider
       */
      function getRecoveryConsumerIds() {
        const consumerIds = [];
        [endpointReference.referencingNamespace, endpointReference.containerNamespace].forEach((consumerId) => {
          if (consumerId && !consumerIds.includes(consumerId)) {
            consumerIds.push(consumerId);
          }
        });
        return consumerIds;
      }

      /**
       * @param {string} consumerId extension id that may depend on the service provider
       * @returns {boolean} true when dependency metadata already exposes the provider
       */
      function isProviderVisibleToConsumer(consumerId) {
        return consumerId === extensionId || registry.isDependent(consumerId, extensionId);
      }

      const consumerIds = getRecoveryConsumerIds();
      if (consumerIds.length && !consumerIds.some(isProviderVisibleToConsumer)) {
        // Services may still be exposed by provider metadata such as publicAccess. Log the dependency error, but
        // recover the provider so the normal service-access checks can make the final decision.
        logger.error('Extension', consumerIds[0], 'references service provider extension', extensionId,
          'without declaring it as a dependency.');
      }

      return registry.loadFullManifest(extensionId)
        .then(() => registerExtensionServices(registry.getExtensionById(extensionId)));
    }

    /**
     * Get an endpoint
     * First, check the active Page; if it has a services object, and that contains the endpoint, return that.
     * Otherwise, check if the Application has any services registered, and contains the endpoint.
     * @param {EndpointReference} endpointReference
     * @param {*} [serviceContext] - not used here (see OperationRefEndpointProvider).
     * @param {object} [serverVariables] - the serverVariables, if any.
     * @return {Promise<Endpoint>}
     * @public
     */
    static getEndpoint(endpointReference, serviceContext, serverVariables) {
      // eslint-disable-next-line no-underscore-dangle
      return ServicesManager._findEndpoint(endpointReference, serverVariables)
        .then((tuple) => tuple && tuple.endpoint);
    }

    /**
     * @typedef {Object} EndpointDefinitionInfo
     * @property path
     * @property [operationDef]
     * @property [serviceDef]
     * @property configInfo
     * @property requestInit
     */
    /**
     * return the service definition (openapi, swagger),
     * and additional information used to fetch the service
     * @param {EndpointReference} endpointReference
     * @param {boolean} [checkAccessible=true] Return found service definition only
     *                                  if it is accessible from the endpoint reference's context
     * @returns {Promise<EndpointDefinitionInfo|*>}
     * @public
     */
    static getDefinitionInfo(endpointReference, checkAccessible = true) {
      return ServicesManager.FindDefinition(endpointReference, undefined, checkAccessible, true)
        .then((foundInfo) => (foundInfo ? {
          service: foundInfo.service,
          requestInit: foundInfo.requestInit,
          serviceDef: foundInfo.serviceJSON,
          endpoint: foundInfo.endpoint,
          configInfo: foundInfo.configInfo, // This is never set!
        } : null));
    }

    /**
     * Used to programmatically register service definitions. These are registered at the Application level.
     * @param serviceProvider
     * @return {void}
     * @public
     */
    static addServiceProvider(serviceProvider) {
      Application.getServices().addServiceProvider(serviceProvider);
    }

    /**
     * find the Service object that contains the declaration for the service ID, by getting the container's
     * array of Services objects, which are all Services objects in the container hierarchy.
     * We then search those Services objects for the Service.
     *
     * Finding the Service does not automatically mean that the referenced endpoint can be loaded/used.
     * Caller needs to check if the referenced endpoint can be accessed after the service definition is fetched.
     *
     * Services:Container :: one:one, each Services contains a map of Service
     *
     * @param {EndpointReference} endpointReference
     * @returns {Promise<Services>} resolved with Services
     */
    static findContainerServices(endpointReference) {
      const endpointNamespace = endpointReference.namespace;
      // for Utils.toFlatUniqueArray to filter out the value it needs to be 'undefined' and not 'null'
      const pageOrFlow = Router.getCurrentPage() || undefined;

      //
      // Endpoints with fully qualified names can be found by their namespace
      //

      if (endpointNamespace) {
        const pageServices = (pageOrFlow && pageOrFlow.getServices());
        const handler = (services, er) => services.containsDeclaration(er)
          .then((result) => (result ? services : null));

        // A page can own a qualified service namespace that is not an extension id. Check it before attempting
        // extension recovery, which is intentionally strict when the requested extension is absent.
        const pageServicesPromise = (pageServices && pageServices.namespace === endpointNamespace)
          ? handler(pageServices, endpointReference)
          : Promise.resolve(null);

        // eslint-disable-next-line no-underscore-dangle
        return pageServicesPromise.then((matchingPageServices) => {
          if (matchingPageServices) {
            return matchingPageServices;
          }

          return ServicesManager._getExtensionServices(endpointReference).catch((error) => {
            // Extension recovery is strict so direct callers can diagnose a malformed full manifest. Endpoint
            // lookup, however, has always represented an unknown optional service by resolving without an endpoint.
            logger.warn('Unable to recover extension services for namespace', endpointNamespace, error);
            return undefined;
          }).then((extensionServices) => {
            const servicesArray = Utils.toFlatUniqueArray(extensionServices)
              .filter((s) => s.namespace === endpointNamespace);

            return servicesArray.reduce(
              (promise, services) => promise.then((result) => result || handler(services, endpointReference)),
              Promise.resolve(),
            );
          });
        });
      }

      //
      // Endpoints without the namespace have to be found based on the calling context (i.e. container)
      //

      const contextNamespace = endpointReference.containerNamespace;

      const allServices = (pageOrFlow && pageOrFlow.getAllServices()) || Application.getAllServices();

      // These Services objects are starting points for the handler's search/traversal
      const servicesArray = allServices.filter((s) => s.namespace === contextNamespace);

      if (!endpointReference.isProgrammatic) {
        logger.warn('Resolving not fully qualified endpoint reference:',
          JSON.stringify(endpointReference));
        if (logger.isInfo) {
          logger.info('Searching for it starting from the services from extensions:',
            JSON.stringify(servicesArray.map((s) => s.namespace)));
        }
      }

      // Look for the endpointReference on the traversed "services" as well as its delegate services.
      // Using a set to avoid looking for the endpoint reference in the same "services".

      const set = new Set();
      const handler = (services, er) => {
        if (set.has(services)) {
          return false;
        }
        set.add(services);
        return Promise.resolve()
          .then(() => services.isEndpointReferenceCompatible(endpointReference) && services.containsDeclaration(er))
          .then((result) => (result ? services : services.searchDelegatesAsync((delegate) => handler(delegate, er))));
      };

      return servicesArray.reduce(
        (promise, services) => promise.then((result) => result || handler(services, endpointReference)),
        Promise.resolve(),
      );
    }

    /**
     * Finds instance of the Services that should be queried for services in the given context.
     *
     * @param {*} servicesContext ServiceResolver's context
     * @returns {Services}
     */
    static findContextServices(servicesContext) {
      if (servicesContext) {
        // it is container
        if (typeof servicesContext.getServices === 'function') {
          return servicesContext.getServices();
        }

        // or at least has an extensionId
        const { extensionId } = servicesContext;
        if (extensionId) {
          const extensions = Application.extensionsArray || [];
          const extension = extensions.find((ext) => ext.extensionId === extensionId);
          if (extension) {
            return extension.services;
          }
        }
      }

      return Application.getServices();
    }

    /**
     * Returns the canonical cache key used for a loaded service definition.
     *
     * The returned key matches the identity VB runtime uses internally for the
     * loaded-service cache, so it is suitable for external caching of processed metadata.
     *
     * @param {EndpointReference} endpointReference Endpoint reference identifying the service.
     * @returns {Promise<string|null>} Canonical loaded-service cache key, or null if the service cannot be resolved.
     */
    static async getServiceCacheKey(endpointReference) {
      const services = await ServicesManager.findContainerServices(endpointReference);
      if (!services) {
        return null;
      }

      return services.buildLoadedServiceCacheKey(endpointReference);
    }

    /**
     * search declared service definitions for the endpoint (serviceId/operationId)
     *
     * @param endpointReference {EndpointReference}
     * @param {object} [serverVariables] - the serverVariables, if any.
     * @returns {Promise} resolved with {endpoint}
     * @private
     */
    static _findEndpoint(endpointReference, serverVariables) {
      return ServicesManager.findContainerServices(endpointReference)
        .then((services) => services && services.getEndpoint(endpointReference, serverVariables))
        .then((endpoint) => endpoint && { endpoint });
    }

    /**
     * Finds definition of EndpointReference, loading it if not already loaded.
     * It returns object with the ServiceDefinition and the Endpoint if it is found.
     *
     * @param {EndpointReference} endpointReference
     * @param {boolean} [checkAccessible=true] Return found service definition only
     *                                  if it is accessible from the endpoint reference's context
     * @returns {Promise<Object|null>} resolved with the Swagger/OpenApi from the ServiceDefinition
     */
    static async FindDefinition(endpointReference, serverVariables, checkAccessible = true, extraInfo = false) {
      if (!endpointReference.serviceId) {
        // this should NEVER happen
        throw new
        Error(`An EndpointReference with no service ID was used, trying to find a service: ${endpointReference}`);
      }

      try {
        const services = await ServicesManager.findContainerServices(endpointReference);
        if (!services) {
          return null;
        }

        const serviceId = endpointReference.getQualifiedServiceId(services.namespace);
        const serviceDefinition = await services.LoadService(serviceId, false,
          endpointReference, serverVariables);
        if (!serviceDefinition) {
          return null;
        }
        // we need to load the service to know if it accessible
        // as it can contain metadata describing the rules for accessing it
        if (checkAccessible && !services.isServiceAccessible(serviceDefinition, endpointReference)) {
          // we can not use this service for the endpoint
          return null;
        }

        const definitionStructure = {
          service: serviceDefinition,
          requestInit: serviceDefinition.requestInit,
        };

        // if needed resolve ServiceDef JSON (this may be costly for programmatic endpoints),
        // and the endpoint
        if (extraInfo) {
          // openApi is OpenApiObjectCommon, and its definition is simple JS Object
          const [serviceJSON, endpoint] = await Promise
            .all([serviceDefinition.getDefinition(endpointReference),
              serviceDefinition.findEndpoint(endpointReference)]);

          definitionStructure.serviceJSON = serviceJSON;
          definitionStructure.endpoint = endpoint;
        }

        return definitionStructure;
      } catch (err) { // eslint-disable-line no-unused-vars
        return null;
      }
    }

    // TODO: make FindDefinition accept OperationRef EndpointReference as an input, and consolidate with this method
    /**
     * Finds definition of a service at given location, loading it if not already loaded.
     * It returns object with the ServiceDefinition if it is found, otherwise it returns null.
     *
     * @param {string} openApiUrl
     * @param {*} serviceContext
     * @returns {Promise<Object|null>}
     * @package
     */
    static async FindDefinitionByUrl(openApiUrl, serviceContext) {
      // services model for the given context
      const contextServices = ServicesManager.findContextServices(serviceContext);

      // recursively search for service by its URL
      const set = new Set();
      const handler = (services) => {
        // skip services we already visited
        if (set.has(services)) {
          return null;
        }
        set.add(services);
        return Promise.resolve()
          .then(() => services.LoadServiceFromUrl(openApiUrl))
          // continue search through the delegates if service not found
          .then((result) => (result || services.searchDelegatesAsync((delegate) => handler(delegate))));
      };

      const serviceDefinition = await handler(contextServices);
      if (serviceDefinition) {
        // return same data structure as the FindDefinition.
        return {
          service: serviceDefinition,
          requestInit: serviceDefinition.requestInit,
        };
      }
      return null;
    }

    /**
     * looks for the containing Services in the container hierarchy, and disposes the specific service
     * @param {string} id declared id of the service
     * @returns {Promise<Services>} resolved with Services, or rejected with text message
     */
    static disposeService(id) {
      return Promise.resolve()
        .then(() => ServicesManager.findContainerServices(EndpointReferenceFactory.getServiceOnlyReference(id)))
        .then((services) => {
          if (services) {
            services.disposeService(id);
            return services;
          }

          throw new Error(`unable to find service to dispose: ${id}`);
        });
    }

    /**
     * Find the authentication information for the service's server.
     * This flow has been derived from examining how Rest assembles authentication and how the
     * AuthPreprocessorHandlerPlugin and TokenRelayHandlerPlugin determine authentication headers.
     *
     * @param {string} id
     * @returns {Promise<Object>}
     */
    static async getServiceAuthentication(id) {
      // See if we can find the Definition for the service
      const endpointReference = EndpointReferenceFactory.getServiceOnlyReference(id);
      const info = await ServicesManager.getDefinitionInfo(endpointReference);
      if (!info) {
        return undefined;
      }

      const serviceDefinition = info.service;
      const server = serviceDefinition.server;
      const serverUrl = Utils.addTrailingSlash(server.getUrl());
      // Call the urlMapper directly; the token and proxy urls are ignored by urlMapperClient.
      const urlMapping = await ConfigLoader.urlMapper.getUrlMapping(serverUrl, true /* mapBackends */);
      return urlMapping && urlMapping.authentication && urlMapping.authentication.authenticated;
    }
  }

  return ServicesManager;
});

